import { defineStore } from "pinia";
import {
    ColorValues,
    EmployerThemeState,
    FetchState,
    ManageThemesRequest,
    ManageThemesResponse,
    PartnerThemeState,
    ThemeGetRequest,
    ThemePagedResponse,
    ThemeSearchRequest,
    ThemeState
} from "@/shared/types";
import {
    deletePartnerTheme,
    getActiveThemes,
    getEmployerTheme,
    getPartnerTheme,
    getTheme,
    getThemeForUser,
    saveEmployerTheme,
    savePartnerTheme,
    searchTheme,
    updateTheme
} from "@/shared/api/themeService";
import moment from "moment";
import Vue from "vue";
import { formatStyleString, getColorShifts } from "@/shared/colorAdjuster";
import * as storeHelper from "@/shared/storeHelper";
import { AxiosError } from "axios";
import { EmployerThemeRequest, EmployerThemeResponse, PartnerThemeRequest, PartnerThemeResponse } from "@/admin/types";
import _ from "lodash";
import { useNotifierStore } from "./notifier";
import { usePageSizeStore } from "./pageSize";

const bendColorValues: ColorValues = {
    primary: "#051f47",
    secondary: "#F0545E",
    tertiary: "#3966AD",
    button: "#F0545E",
    navButton: "#3966AD",
    navButtonInverse: "#F0545E",
    primaryFont: "#FFFFFF",
    secondaryFont: "#FFFFFF",
    tertiaryFont: "#FFFFFF",
    buttonFont: "#FFFFFF",
    navButtonFont: "#FFFFFF",
    navButtonFontInverse: "#FFFFFF"
};
const bendFontFace = `font-family: "Avenir", Helvetica, Arial, sans-serif;`;
const defaultEmployerThemeState: EmployerThemeState = {
    employerThemeFetchState: FetchState.Uninitialized,
    employerTheme: {
        employerId: 0,
        themeId: 0,
        employerThemeId: 0
    }
};

const defaultThemeState: PartnerThemeState = {
    partnerThemeFetchState: FetchState.Uninitialized,
    partnerTheme: {
        partnerId: 0,
        themeId: 0,
        partnerThemeId: 0
    }
};

export const initialState: ThemeState = {
    colorStyle: formatStyleString(bendColorValues, bendFontFace),
    colorValues: bendColorValues,
    fontFace: bendFontFace,
    pageNumber: 1,
    totalRecords: 0,
    themePages: [],
    themes: [],
    themeFetchState: FetchState.Uninitialized,
    partnerThemeState: defaultThemeState,
    employerThemeState: defaultEmployerThemeState,
    activeThemeFetchState: FetchState.Uninitialized,
    activeThemes: []
};


export const useThemeStore = defineStore("theme", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("theme"),
    },
    state: (): ThemeState => (_.cloneDeep(initialState)),
    getters: {
        primaryColor: state => state.colorValues.primary,
        primaryColorShifts: state => getColorShifts(state.colorValues.primary),
        secondaryColor: state => state.colorValues.secondary,
        secondaryColorShifts: state => getColorShifts(state.colorValues.secondary),
        tertiaryColor: state => state.colorValues.tertiary,
        tertiaryColorShifts: state => getColorShifts(state.colorValues.tertiary),
        buttonColor: state => state.colorValues.button,
        buttonColorShifts: state => getColorShifts(state.colorValues.button),
        navButtonColor: state => state.colorValues.navButton,
        navButtonColorShifts: state => getColorShifts(state.colorValues.navButton),
        navButtonColorInverse: state => state.colorValues.navButtonInverse,
        navButtonColorShiftsInverse: state => getColorShifts(state.colorValues.navButtonInverse),
        cobrandingLightGrey: state => "#E6EAEE",
        getThemesByPageNumber: state => (pageNumber: number): ManageThemesResponse[] => {
            const pageObject = state.themePages[pageNumber];

            if (typeof pageObject === "undefined") {
                return [];
            }
            return pageObject;
        },
        pageSize () {
            const pageSizeStore = usePageSizeStore();
            return pageSizeStore.pageSize;
        },
        themesLoaded: state => state.themeFetchState === FetchState.Loaded,
        themesFetchLoading: state => state.themeFetchState === FetchState.Loading,
        pageCount: state => {
            const pageSizeStore = usePageSizeStore();
            const pageSize = pageSizeStore.pageSize;
            return state.totalRecords < pageSize ? 1 : Math.floor((state.totalRecords + pageSize - 1) / pageSize);
        },
        themeById: state => (themeId: number) => {
            for (const theme of state.themes) {
                if (theme.themeId === themeId) {
                    return theme;
                }
            }

            return null;
        },
        partnerTheme: state => state.partnerThemeState.partnerTheme,
        partnerThemeIsLoaded: state => state.partnerThemeState.partnerThemeFetchState === FetchState.Loaded,
        employerTheme: state => state.employerThemeState.employerTheme,
        employerThemeIsLoaded: state => state.employerThemeState.employerThemeFetchState,
        activeThemesIsLoaded: state => state.activeThemeFetchState === FetchState.Loaded,
    },
    actions: {
        setColorStyleString (colorStyle: string) {
            this.colorStyle = colorStyle;
        },
        setFontFace (fontType: string) {
            const backup = fontType === "poppins" ? "Century Gothic" : "Helvetica";
            this.fontFace = `font-family: "${fontType}", "${backup}", Arial, sans-serif;`;
        },
        setColorState (themeRequest: ManageThemesRequest) {
            this.colorValues.primary = themeRequest.primaryColor;
            this.colorValues.primaryFont = themeRequest.primaryFontColor;
            this.colorValues.secondary = themeRequest.secondaryColor;
            this.colorValues.secondaryFont = themeRequest.secondaryFontColor;
            this.colorValues.tertiary = themeRequest.tertiaryColor;
            this.colorValues.tertiaryFont = themeRequest.tertiaryFontColor;
            this.colorValues.button = themeRequest.isButtonColorPrimary ? themeRequest.primaryColor
                : themeRequest.secondaryColor;
            this.colorValues.buttonFont = themeRequest.isButtonColorPrimary ? themeRequest.primaryFontColor
                : themeRequest.secondaryFontColor;
            this.colorValues.navButton = themeRequest.isNavButtonColorSecondary ? themeRequest.secondaryColor
                : themeRequest.tertiaryColor;
            this.colorValues.navButtonFont = themeRequest.isNavButtonColorSecondary ? themeRequest.secondaryFontColor
                : themeRequest.tertiaryFontColor;
            this.colorValues.navButtonInverse = themeRequest.isNavButtonColorSecondary ? themeRequest.tertiaryColor
                : themeRequest.secondaryColor;
            this.colorValues.navButtonFontInverse = themeRequest.isNavButtonColorSecondary ? themeRequest.tertiaryFontColor
                : themeRequest.secondaryFontColor;
        },
        setDefaultColors () {
            this.colorValues = { ...bendColorValues };
            this.colorStyle = formatStyleString(this.colorValues, this.fontFace);
        },
        clearThemePages () {
            this.themePages = [];
            this.totalRecords = 0;
        },
        setThemeFetchState (fetchState: FetchState) {
            this.themeFetchState = fetchState;
        },
        setThemes (themeList: ThemePagedResponse) {
            for (const theme of themeList.themeList) {
                theme.dateCreated = moment(theme.dateCreated).format("MM/DD/YYYY");
                theme.dateLastModified = moment(theme.dateLastModified).format("MM/DD/YYYY");
            }

            // A new record was added by another system user so we need to refresh all the stored pages.
            if (this.totalRecords !== themeList.totalRecords) {
                this.themePages = [];
            }

            const themePages = [ ...this.themePages ];
            themePages[themeList.pageNumber] = themeList.themeList;

            this.totalRecords = themeList.totalRecords;
            this.pageNumber = themeList.pageNumber;
            this.themePages = themePages;

            this.themes = this.themes.concat(themeList.themeList);
        },
        setTheme (theme: ManageThemesResponse) {
            const index = this.themes.findIndex(e => e.themeId === theme.themeId);

            if (index >= 0) {
                Vue.set(this.themes, index, theme);
            } else {
                this.themes = [ theme, ...this.themes ];
            }
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        setPartnerTheme (partnerThemeResponse: PartnerThemeResponse) {
            this.partnerThemeState.partnerTheme = partnerThemeResponse;
        },
        setPartnerThemeFetchState (fetchState: FetchState) {
            this.partnerThemeState.partnerThemeFetchState = fetchState;
        },
        setEmployerTheme (employerThemeResponse: EmployerThemeResponse) {
            this.employerThemeState.employerTheme = employerThemeResponse;
        },
        setEmployerThemeFetchState (fetchState: FetchState) {
            this.employerThemeState.employerThemeFetchState = fetchState;
        },
        setActiveThemes (response: ManageThemesResponse[]) {
            this.activeThemes = response;
        },
        setActiveThemesFetchState (fetchState: FetchState) {
            this.activeThemeFetchState = fetchState;
        },
        getTheme ({ employerId, partnerId, forceUpdate }: ThemeGetRequest) {
            if (forceUpdate) {
                this.setThemeFetchState(FetchState.Uninitialized);
            }

            if (this.themeFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setThemeFetchState(FetchState.Loading);

            return getThemeForUser(employerId, partnerId).then(response => {
                this.setThemeFetchState(FetchState.Loaded);
                this.setColorState(response.data);
                this.setFontFace(response.data.fontType);
                this.setColorStyleString(formatStyleString(this.colorValues, this.fontFace));
            }).catch(err => {
                this.setThemeFetchState(FetchState.Loaded);
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        updateTheme (request: ManageThemesRequest) {
            const notifierStore = useNotifierStore();
            return updateTheme(request)
                .then(response => {
                    this.setTheme(request);
                    notifierStore.setSuccess(response.data.outcome);
                })
                .catch(error => {
                    notifierStore.setDanger(error.response.data.errorMessage);
                });
        },
        searchThemes (clearState: boolean, pageNumber: number, searchTerm: string) {
            const pageSizeStore = usePageSizeStore();
            return new Promise<void>((resolve, reject) => {
                const pageSize = pageSizeStore.pageSize;
                const searchRequest: ThemeSearchRequest = { pageNumber, pageSize, searchTerm };

                if (clearState) {
                    this.clearThemePages();
                    this.setThemeFetchState(FetchState.Loading);
                    searchTheme(searchRequest)
                        .then(response => {
                            this.setThemes(response.data);
                            this.setThemeFetchState(FetchState.Loaded);
                            resolve();
                        }).catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                    });
                }

                const pageObject = this.themePages[pageNumber || 1];
                if (typeof pageObject !== "undefined") {
                    resolve();
                    return;
                } else {
                    this.setThemeFetchState(FetchState.Loading);
                    searchTheme(searchRequest)
                        .then(response => {
                            this.setThemes(response.data);
                            this.setThemeFetchState(FetchState.Loaded);
                            resolve();
                        }).catch(err => {
                        storeHelper.handleError({ error: err, rethrow: true });
                    });
                }
            });
        },
        loadTheme (themeId: number): Promise<ManageThemesResponse> {
            const existingTheme = _.find(this.themes, x => x.themeId === themeId);

            if (existingTheme) {
                return Promise.resolve(existingTheme);
            }

            return getTheme(themeId)
                .then(r => {
                    this.setTheme(r.data);
                    return Promise.resolve(r.data);
                })
                .catch((err: AxiosError | any) => {
                    const displayError = (err.response.status === 404) ?
                        `Theme with id ${themeId} not found` : err;

                    storeHelper.handleError({ error: displayError, rethrow: false });
                    return Promise.reject();
                });
        },
        getPartnerTheme (partnerId: number | string) {
            if (this.partnerThemeState.partnerThemeFetchState === FetchState.Loaded
                && this.partnerThemeState.partnerTheme.partnerId === partnerId) {
                return;
            }
            this.setPartnerThemeFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();
            return getPartnerTheme(partnerId)
                .then(response => {
                    this.setPartnerTheme(response.data);
                    this.setPartnerThemeFetchState(FetchState.Loaded);
                })
                .catch(error => {
                    notifierStore.setDanger(error.response.data.errorMessage);
                });
        },
        getEmployerTheme (employerId: number) {
            if (this.employerThemeState.employerThemeFetchState === FetchState.Loaded
                && this.employerThemeState.employerTheme.employerId === employerId) {
                return;
            }
            this.setEmployerThemeFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();
            return getEmployerTheme(employerId)
                .then(response => {
                    this.setEmployerTheme(response.data);
                    this.setEmployerThemeFetchState(FetchState.Loaded);
                })
                .catch(error => {
                    notifierStore.setDanger(error.response.data.errorMessage);
                });
        },
        savePartnerTheme (request: PartnerThemeRequest) {
            this.setPartnerThemeFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();
            return savePartnerTheme(request)
                .then(response => {
                    this.setPartnerTheme(response.data);
                    this.setPartnerThemeFetchState(FetchState.Loaded);
                    notifierStore.setSuccess("Partner theme is saved successfully.");
                })
                .catch(error => {
                    notifierStore.setDanger(error.response.data.errorMessage);
                });
        },
        saveEmployerTheme (request: EmployerThemeRequest) {
            this.setEmployerThemeFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();
            return saveEmployerTheme(request)
                .then(response => {
                    this.setEmployerTheme(response.data);
                    this.setEmployerThemeFetchState(FetchState.Loaded);
                    notifierStore.setSuccess("Employer theme is updated successfully.");
                })
                .catch(error => {
                    notifierStore.setDanger(error.response.data.errorMessage);
                });
        },
        deletePartnerTheme (request: PartnerThemeRequest) {
            this.setPartnerThemeFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();
            return deletePartnerTheme(request)
                .then(response => {
                    this.setPartnerTheme(response.data);
                    this.setPartnerThemeFetchState(FetchState.Loaded);
                    notifierStore.setSuccess("Partner theme is removed successfully.");
                })
                .catch(error => {
                    notifierStore.setDanger(error.response.data.errorMessage);
                });
        },
        getActiveThemes () {
            if (this.activeThemeFetchState !== FetchState.Uninitialized) {
                return;
            }
            this.setActiveThemesFetchState(FetchState.Loading);
            const notifierStore = useNotifierStore();
            return getActiveThemes()
                .then(response => {
                    this.setActiveThemes(response.data);
                    this.setActiveThemesFetchState(FetchState.Loaded);
                })
                .catch(error => {
                    notifierStore.setDanger(error.response.data.errorMessage);
                });
        },
    }
});